/**
 * Pure helpers of the `wizard_question` assistant view (MEIS-13672): the guard
 * for the stored spec, the mapping from the native renderers' item action to
 * the `answer_quote_wizard_question` tool's answer union, the readable answer
 * line and the chat message (line + `agent-form` block) a submission sends.
 */
import type {
  ApplyItemActionInput,
  EnterItemProps2ValueEntry,
  WizardQuestionStrategy,
} from "../../../../clients/graphqlTypes.js";
import type { WizardQuestions_QuestionFragment } from "../../documents/WizardQuestions.generated.js";
import { getQuestionPrompt } from "../../quotes/wizard/questionShell.js";
import { appendFormAnswerBlock } from "./agentFormSpec.js";
import type { AgentViewSpec } from "./registry.js";

export type WizardQuestionMode =
  | "item"
  | "onQuoteCreate"
  | "onQuoteRefinement"
  | "onQuoteRefinement2"
  | "onQuoteRefinement3";

export type WizardQuestionKind =
  | "properties"
  | "decision"
  | "contingency"
  | "product"
  | "recommendations"
  | "placeholderForTemplate";

export type WizardQuestionViewSpec = {
  component: "wizard_question";
  quoteId: string;
  projectId: string;
  mode: WizardQuestionMode;
  strategy: WizardQuestionStrategy;
  questionId: string;
  kind: WizardQuestionKind;
  prompt: string;
  itemTitle: string;
  memory: string;
};

export type WizardPropertyValue =
  | string
  | number
  | boolean
  | string[]
  | number[]
  | null;

export type WizardAnswer =
  | {
      kind: "properties";
      values: Array<{ key: string; value: WizardPropertyValue }>;
    }
  | { kind: "decision"; selectedOptionIds: string[] }
  | { kind: "contingency"; include: boolean }
  | {
      kind: "product";
      product: { supplierId: string; articleNumber: string } | null;
    }
  | {
      kind: "recommendations";
      recommendations: Array<{ recommendationId: string; include: boolean }>;
    }
  | { kind: "placeholderForTemplate"; templateId: string | null };

/** The words the readable line needs from the translations. */
export type WizardAnswerLabels = { yes: string; no: string };

const MODES: readonly WizardQuestionMode[] = [
  "item",
  "onQuoteCreate",
  "onQuoteRefinement",
  "onQuoteRefinement2",
  "onQuoteRefinement3",
];
const KINDS: readonly WizardQuestionKind[] = [
  "properties",
  "decision",
  "contingency",
  "product",
  "recommendations",
  "placeholderForTemplate",
];
const STRATEGIES: readonly WizardQuestionStrategy[] = [
  "ITEM_EACH",
  "ON_QUOTE_CREATE",
  "ON_QUOTE_REFINEMENT",
  "ON_QUOTE_REFINEMENT_2",
  "ON_QUOTE_REFINEMENT_3",
];

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0;

/** Stored JSON: every field is checked before the view trusts it. */
export function isWizardQuestionView(
  view: AgentViewSpec
): view is AgentViewSpec & WizardQuestionViewSpec {
  return (
    view.component === "wizard_question" &&
    isNonEmptyString(view.quoteId) &&
    isNonEmptyString(view.projectId) &&
    isNonEmptyString(view.questionId) &&
    // An untitled item legitimately yields an empty prompt and item title.
    typeof view.prompt === "string" &&
    typeof view.itemTitle === "string" &&
    typeof view.memory === "string" &&
    MODES.some(mode => mode === view.mode) &&
    KINDS.some(kind => kind === view.kind) &&
    STRATEGIES.some(strategy => strategy === view.strategy)
  );
}

function unboxEntry(
  entry: EnterItemProps2ValueEntry
): { key: string; value: WizardPropertyValue } | null {
  if (entry.text)
    return { key: entry.text.key, value: entry.text.valueText ?? null };
  if (entry.textArray)
    return {
      key: entry.textArray.key,
      value: entry.textArray.valueTextArray ?? null,
    };
  if (entry.number)
    return { key: entry.number.key, value: entry.number.valueNumber ?? null };
  if (entry.numberArray)
    return {
      key: entry.numberArray.key,
      value: entry.numberArray.valueNumberArray ?? null,
    };
  if (entry.bool)
    return { key: entry.bool.key, value: entry.bool.valueBool ?? null };
  return null;
}

/**
 * The tool answer for the action a staged renderer submits, or `null` for an
 * action this view does not stage (product and template save themselves). The
 * empty-value filter already ran in the renderer's `propertiesAction`.
 */
export function itemActionToWizardAnswer(
  action: ApplyItemActionInput
): WizardAnswer | null {
  if (action.setEnterPropsValueEntries) {
    const values: Array<{ key: string; value: WizardPropertyValue }> = [];
    for (const entry of action.setEnterPropsValueEntries.entries) {
      const unboxed = unboxEntry(entry);
      if (unboxed) values.push(unboxed);
    }
    return { kind: "properties", values };
  }
  if (action.setDecisionSubitemsPreselection)
    return {
      kind: "decision",
      selectedOptionIds:
        action.setDecisionSubitemsPreselection.decisionSubitemsPreselection,
    };
  if (action.setDecisionContingencyPreselection)
    return {
      kind: "contingency",
      include:
        action.setDecisionContingencyPreselection
          .decisionContingentItemPreselection === true,
    };
  if (action.setRecommendations)
    return {
      kind: "recommendations",
      recommendations: action.setRecommendations.recommendations.map(
        ({ recommendationId, operation }) => ({
          recommendationId,
          include: operation === "include",
        })
      ),
    };
  return null;
}

const NONE = "–";

function propertyValueText(
  value: WizardPropertyValue,
  labels: WizardAnswerLabels
): string {
  if (typeof value === "boolean") return value ? labels.yes : labels.no;
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

function valueText(
  question: WizardQuestions_QuestionFragment | null,
  answer: WizardAnswer,
  labels: WizardAnswerLabels,
  pickedTitle?: string | null
): string {
  switch (answer.kind) {
    case "decision": {
      const options =
        question?.__typename === "WizardDecisionQuestion"
          ? question.options
          : [];
      const titles = answer.selectedOptionIds.map(
        id => options.find(o => o.itemId === id)?.title ?? id
      );
      return titles.length > 0 ? titles.join(", ") : NONE;
    }
    case "contingency":
      return answer.include ? labels.yes : labels.no;
    case "properties": {
      const props =
        question?.__typename === "WizardMultiplePropertiesQuestion"
          ? question.props
          : [];
      const pairs = answer.values
        .filter(entry => entry.value !== null)
        .map(entry => {
          const label =
            props.find(p => p.prop.key === entry.key)?.prop.label ?? entry.key;
          return `${label}: ${propertyValueText(entry.value, labels)}`;
        });
      return pairs.length > 0 ? pairs.join("; ") : NONE;
    }
    case "recommendations": {
      const recommendations =
        question?.__typename === "WizardMultipleRecommendationsQuestion"
          ? question.recommendations
          : [];
      const titles = answer.recommendations
        .filter(r => r.include)
        .map(r => {
          const found = recommendations.find(rec =>
            rec.__typename === "WizardRecommendationTemplate"
              ? rec.templateId === r.recommendationId
              : rec.templateTypeId === r.recommendationId
          );
          if (!found) return r.recommendationId;
          return found.__typename === "WizardRecommendationTemplate"
            ? found.templateTitle
            : found.templateTypeTitle;
        });
      return titles.length > 0 ? titles.join(", ") : NONE;
    }
    case "product":
      return answer.product
        ? pickedTitle || answer.product.articleNumber
        : NONE;
    case "placeholderForTemplate":
      return answer.templateId === null
        ? NONE
        : pickedTitle || answer.templateId;
  }
}

/** `<prompt>: <value text>`, the line the user sees in the chat. */
export function wizardAnswerText(
  question: WizardQuestions_QuestionFragment | null,
  answer: WizardAnswer,
  labels: WizardAnswerLabels,
  fallbackPrompt = "",
  pickedTitle?: string | null
): string {
  const prompt = question ? getQuestionPrompt(question) : fallbackPrompt;
  return `${prompt}: ${valueText(question, answer, labels, pickedTitle)}`;
}

/** The message a submission sends: the readable line plus the `agent-form` block the tool reads. */
export function buildWizardAnswerMessage(
  view: Pick<
    WizardQuestionViewSpec,
    "quoteId" | "mode" | "questionId" | "prompt"
  >,
  question: WizardQuestions_QuestionFragment | null,
  answer: WizardAnswer,
  labels: WizardAnswerLabels,
  options: { savedInView?: boolean; pickedTitle?: string | null } = {}
): string {
  const text = wizardAnswerText(
    question,
    answer,
    labels,
    view.prompt,
    options.pickedTitle
  );
  return appendFormAnswerBlock(text, {
    quoteId: view.quoteId,
    mode: view.mode,
    questionId: view.questionId,
    answer,
    ...(options.savedInView ? { savedInView: true } : {}),
  });
}
